<template>
  <div class="coaching">
    <component
      :is="currentLayout"
      :topic-title="topicTitle"
      :scenario-title="scenarioTitle"
      :show-class-roll="showClassRoll"
    >
      <template #board>
        <QuizSession
          v-if="isQuizModeActive"
          :scenarios="quizScenarios"
          :is-presentation-mode="isPresentationMode"
          @exitQuiz="exitQuiz"
        />
        <div v-else>
          <ScenarioFree
            v-if="isFreeModeActive"
            :fen="freeModeInitialFen"
            :is-presentation-mode="isPresentationMode"
            @exitFreeMode="
              exitFreeMode();
              reloadSavedScenario();
            "
            @loadScenarioFromFen="loadScenarioFromFen"
          />
          <div v-if="!isFreeModeActive">
            <ScenarioExample
              v-if="!scenario.isPuzzle"
              :pgn="scenario.pgn"
              :is-presentation-mode="isPresentationMode"
              :is-custom-scenario="isCustomScenario"
              @enterFreeMode="enterFreeMode"
              @edit="handleEditScenario(selectedScenario, $event)"
              @exit="reloadSavedScenario"
            />
            <ScenarioPuzzle
              v-if="scenario.isPuzzle && !isQuizModeActive"
              :pgn="scenario.pgn"
              :is-presentation-mode="isPresentationMode"
              @enterFreeMode="enterFreeMode"
            />
          </div>
        </div>
      </template>

      <template #controls>
        <ScenarioNavigator
          v-show="!isQuizModeActive"
          :topic-index="topicIndex"
          :topic-count="topics.length"
          :scenario-index="scenarioIndex"
          :scenario-count="scenarios.length"
          :is-quiz-available="isQuizAvailable"
          :show-moves-table="getShowMoveTable"
          :is-presentation-mode="isPresentationMode"
          @navigateTopic="handleNavigateTopic"
          @openTopicSelector="openTopicSelector"
          @navigateScenario="handleNavigateScenario"
          @previewScenarios="isPreviewModalActive = true"
          @editScenario="handleEditScenario(selectedScenario)"
          @customFen="isCustomFenModalActive = true"
          @toggleMovesTable="toggleMovesTable"
          @startQuiz="startQuiz"
          @togglePresentation="togglePresentationMode"
        />
        <slot name="additional-controls"></slot>
      </template>

      <template #keyboard-help>
        <KeyboardShortcutsHelp :shortcuts-map="keyboardShortcuts" />
      </template>

      <template #classroll>
        <slot name="classroll"></slot>
      </template>
    </component>

    <!-- Modals -->
    <b-modal
      :active.sync="isScenarioEditorActive"
      has-modal-card
      trap-focus
      :destroy-on-hide="true"
      @keydown.stop
    >
      <ScenarioEditor
        :initialPgn="currentPgnToEdit"
        :originalScenario="scenario"
        :current-topic-id="selectedTopicId"
        :load-private-only="true"
        @close="isScenarioEditorActive = false"
        @loadScenarioFromPgn="loadScenarioFromPgn"
        @updateScenario="handleUpdateScenario"
        @saveScenarioToTopic="handleSaveScenarioToTopic"
      />
    </b-modal>

    <b-modal
      :active.sync="isCustomFenModalActive"
      has-modal-card
      trap-focus
      :destroy-on-hide="true"
      @keydown.stop
    >
      <FenPositionEditor
        @close="isCustomFenModalActive = false"
        @fen-loaded="loadScenarioFromFen"
      />
    </b-modal>

    <b-modal
      :active.sync="isTopicSelectorActive"
      has-modal-card
      trap-focus
      :destroy-on-hide="true"
      :width="1100"
    >
      <TopicSelector
        :topics="topics"
        :selected-topic-id="selectedTopicId"
        :selected-scenario-id="selectedScenarioId"
        :get-or-fetch-scenarios="getOrFetchScenarios"
        @edit="handleEditTopic"
        @preview="handlePreviewTopic"
        @select="handleSelectTopic"
        @selectScenario="handleSelectScenario"
        @close="isTopicSelectorActive = false"
      />
    </b-modal>

    <b-modal
      :active.sync="isPreviewModalActive"
      has-modal-card
      trap-focus
      :destroy-on-hide="true"
    >
      <ScenariosSelector
        :scenarios="scenarios"
        :selected-scenario-id="selectedScenarioId"
        @select="handleSelectScenario($event.id)"
        @close="isPreviewModalActive = false"
      />
    </b-modal>
  </div>
</template>

<script>
import CoachingRenderless from "./CoachingRenderless";
import FenPositionEditor from "@/components/training/FenPositionEditor.vue";
import KeyboardShortcutsHelp from "@/components/common/KeyboardShortcutsHelp.vue";
import QuizSession from "@/components/training/quizzes/QuizSession.vue";
import ScenarioEditor from "@/components/training/scenarios/ScenarioEditor.vue";
import ScenarioExample from "@/components/training/scenarios/ScenarioExample.vue";
import ScenarioFree from "@/components/training/scenarios/ScenarioFree.vue";
import ScenarioNavigator from "@/components/training/scenarios/ScenarioNavigator.vue";
import ScenarioPuzzle from "@/components/training/scenarios/ScenarioPuzzle.vue";
import ScenariosSelector from "@/components/training/scenarios/ScenariosSelector.vue";
import TopicSelector from "../topics/TopicSelector.vue";
import DefaultLayout from "./layouts/DefaultLayout.vue";
import PresentationLayout from "./layouts/PresentationLayout.vue";
import ClassRoll from "@/components/teacher/ClassRoll.vue";

export default {
  name: "Coaching",

  extends: CoachingRenderless,

  components: {
    KeyboardShortcutsHelp,
    QuizSession,
    ScenarioExample,
    ScenarioFree,
    ScenarioPuzzle,
    ScenarioNavigator,
    ScenarioEditor,
    ScenariosSelector,
    FenPositionEditor,
    TopicSelector,
    DefaultLayout,
    PresentationLayout,
    ClassRoll,
  },

  props: {
    showClassRoll: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isPresentationMode: false,
      keyboardShortcuts: {
        "Move navigation + Board Controls": {
          "← / →": "Previous move / Next move",
          "Shift + ← / Shift + →": "Go to start position / Go to end position",
          "↑ / ↓": "Previous variation / Next variation",
          O: "Flip board Orientation",
        },
        "Example Navigation": {
          "[ / ] / \\": "Previous Topic / Next Topic / Topic List",
          "; / ' / ⏎": "Previous Example / Next Example / Preview Examples",
          E: "Edit current Example",
          C: "Custom FEN",
          M: "Toggle Moves table",
          T: "Start Quiz (when available)",
          F11: "Toggle presentation mode",
        },
        "Piece Selection (Free mode only)": {
          X: "Toggle eraser mode",
          S: "Toggle piece color (White/Black)",
          "K/Q/R/B/N/P": "Select King / Queen / Rook / Bishop / Knight / Pawn",
          Esc: "Clear selection / Close modals",
        },
      },
    };
  },

  computed: {
    currentLayout() {
      return this.isPresentationMode ? "PresentationLayout" : "DefaultLayout";
    },

    topicTitle() {
      if (this.isFreeModeActive) return "";
      return this.scenario?.topic?.title || "";
    },

    scenarioTitle() {
      if (this.isQuizModeActive) return "Quiz";
      if (this.isFreeModeActive) return "Free Mode";

      return (
        (this.scenario?.title || "Select an example") +
        (this.scenario?.isPuzzle ? " 🧩" : "") +
        (this.scenario?.isHomework ? " 📚" : "")
      );
    },
  },

  mounted() {
    // If this is a new window, automatically enter presentation mode
    if (window.opener !== null) {
      this.isPresentationMode = true;
      this.$nextTick(() => {
        document.body.dispatchEvent(new Event("chessground.resize"));
      });
    }
  },

  methods: {
    ...CoachingRenderless.methods,

    togglePresentationMode() {
      // Open a new window with the same URL when entering presentation mode
      // Only open a new window if this is not already a new window
      if (!this.isPresentationMode) {
        const currentUrl = window.location.href;
        const windowFeatures =
          "menubar=no,toolbar=no,location=no,resizable=yes,scrollbars=yes,status=no,width=1224,height=768";
        window.open(currentUrl, "chessPresentation", windowFeatures);
      } else {
        if (window.opener !== null) {
          window.close();
          return;
        }
      }
    },
  },
};
</script>

<style scoped>
@import "../../../assets/styles/chess-board-coords.css";
</style>
